import { Avatar, AvatarGroup, Box, Button } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { isArray, isNumber } from 'lodash';
import { useMemo } from 'react';

import { useSxStyles } from '~/components/SolaForceDataGrid/components/ButtonCellComponent/useStyles';
import { useAllEmployees } from '~/data/employees';
import { Employees } from '~/data/employees/types';
import { filterNotNullish, getProfilePictureUrl } from '~/utils/helper';

type PropsType = GridRenderCellParams & {
  pictureOnly?: boolean;
  max?: number;
  onClick?: (value: GridRenderCellParams['value']) => void;
  disabled?: boolean;
};

enum EmployeeCellMode {
  None = 'none',
  Single = 'single',
  Multiple = 'multiple',
}

const EmployeeAvatar = (props: {
  employee: Employees;
  pictureOnly?: boolean;
}) => {
  const { employee, pictureOnly } = props;

  return (
    <>
      <Avatar
        src={getProfilePictureUrl(employee.fEmpProfPictureId)}
        alt={
          !!pictureOnly
            ? `${employee?.fEmpFirstName} ${employee?.fEmpLastName}`
            : undefined
        }
        sx={{ width: 24, height: 24 }}
      />
      {!pictureOnly && (
        <Box>{`${employee?.fEmpFirstName} ${employee?.fEmpLastName}`}</Box>
      )}
    </>
  );
};

export const EmployeeCellComponent = (props: PropsType) => {
  const { value, max, pictureOnly, onClick, disabled } = props;

  const sxStyles = useSxStyles();

  const { allEmployees } = useAllEmployees();

  const { employees, mode } = useMemo(() => {
    const getEmployeeData = (employeeId?: number) => {
      return !!employeeId ? allEmployees[employeeId] : undefined;
    };

    const employeeIds = (
      !!value ? (isArray(value) ? value : [value]) : []
    ).filter(isNumber);

    const employees: Employees[] = filterNotNullish(
      employeeIds.map(getEmployeeData),
    );

    const resolveMode = (count: number) => {
      if (count > 1) {
        return EmployeeCellMode.Multiple;
      } else if (count === 1) {
        return EmployeeCellMode.Single;
      } else {
        return EmployeeCellMode.None;
      }
    };

    return {
      employees,
      mode: resolveMode(employees.length),
    };
  }, [allEmployees, value]);

  switch (mode) {
    case EmployeeCellMode.Multiple: {
      return (
        <AvatarGroup
          max={max ?? 4}
          sx={{
            '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 15 },
          }}
        >
          {employees.map((employee, index) => {
            return (
              <Avatar
                key={`employee-${index}`}
                alt={`${employee?.fEmpFirstName} ${employee?.fEmpLastName}`}
                src={getProfilePictureUrl(employee.fEmpProfPictureId)}
              />
            );
          })}
        </AvatarGroup>
      );
    }
    case EmployeeCellMode.Single: {
      const employee = employees[0];

      return !!onClick && !disabled ? (
        <Button
          onClick={() => {
            onClick(value);
          }}
          sx={sxStyles.cellButton}
        >
          <EmployeeAvatar employee={employee} pictureOnly={pictureOnly} />
        </Button>
      ) : (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <EmployeeAvatar employee={employee} pictureOnly={pictureOnly} />
        </Box>
      );
    }
    case EmployeeCellMode.None:
    default:
      return <></>;
  }
};
